// src/utils/qr.js
// Small QR code generator with NO extra npm package (byte mode, versions 1-10).
// Enough for SKU / UDI texts of up to ~150 characters.
//
//   const { size, modules } = makeQr("hello", "M");
//   modules[y][x] === true  ->  dark square
//
// Error correction levels: "L", "M", "Q", "H".

const ECC = {
  L: { index: 0, formatBits: 1 },
  M: { index: 1, formatBits: 0 },
  Q: { index: 2, formatBits: 3 },
  H: { index: 3, formatBits: 2 },
};

const MAX_VERSION = 10;

// index = version (index 0 unused)
const ECC_CODEWORDS_PER_BLOCK = [
  [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18], // L
  [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26], // M
  [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24], // Q
  [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28], // H
];

const NUM_ERROR_CORRECTION_BLOCKS = [
  [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4], // L
  [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5], // M
  [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8], // Q
  [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8], // H
];

const getBit = (value, index) => ((value >>> index) & 1) !== 0;

const toUtf8Bytes = (text) => {
  const bytes = [];
  const encoded = unescape(encodeURIComponent(String(text)));
  for (let i = 0; i < encoded.length; i += 1) bytes.push(encoded.charCodeAt(i));
  return bytes;
};

const getNumRawDataModules = (ver) => {
  let result = (16 * ver + 128) * ver + 64;
  if (ver >= 2) {
    const numAlign = Math.floor(ver / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (ver >= 7) result -= 36;
  }
  return result;
};

const getNumDataCodewords = (ver, ecl) =>
  Math.floor(getNumRawDataModules(ver) / 8) -
  ECC_CODEWORDS_PER_BLOCK[ecl.index][ver] * NUM_ERROR_CORRECTION_BLOCKS[ecl.index][ver];

/* ---------- Reed-Solomon over GF(256) ---------- */
const gfMultiply = (x, y) => {
  let z = 0;
  for (let i = 7; i >= 0; i -= 1) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z;
};

const rsComputeDivisor = (degree) => {
  const result = [];
  for (let i = 0; i < degree - 1; i += 1) result.push(0);
  result.push(1);
  let root = 1;
  for (let i = 0; i < degree; i += 1) {
    for (let j = 0; j < result.length; j += 1) {
      result[j] = gfMultiply(result[j], root);
      if (j + 1 < result.length) result[j] ^= result[j + 1];
    }
    root = gfMultiply(root, 0x02);
  }
  return result;
};

const rsComputeRemainder = (data, divisor) => {
  const result = divisor.map(() => 0);
  data.forEach((b) => {
    const factor = b ^ result.shift();
    result.push(0);
    divisor.forEach((coef, i) => {
      result[i] ^= gfMultiply(coef, factor);
    });
  });
  return result;
};

/* ---------- data codewords ---------- */
const buildDataCodewords = (bytes, ver, ecl) => {
  const bits = [];
  const append = (value, length) => {
    for (let i = length - 1; i >= 0; i -= 1) bits.push((value >>> i) & 1);
  };

  append(0x4, 4); // byte mode
  append(bytes.length, ver <= 9 ? 8 : 16);
  bytes.forEach((b) => append(b, 8));

  const capacityBits = getNumDataCodewords(ver, ecl) * 8;
  append(0, Math.min(4, capacityBits - bits.length));
  append(0, (8 - (bits.length % 8)) % 8);

  for (let pad = 0xec; bits.length < capacityBits; pad ^= 0xec ^ 0x11) append(pad, 8);

  const codewords = [];
  for (let i = 0; i < bits.length; i += 8) {
    let value = 0;
    for (let j = 0; j < 8; j += 1) value = (value << 1) | bits[i + j];
    codewords.push(value);
  }
  return codewords;
};

const addEccAndInterleave = (data, ver, ecl) => {
  const numBlocks = NUM_ERROR_CORRECTION_BLOCKS[ecl.index][ver];
  const blockEccLen = ECC_CODEWORDS_PER_BLOCK[ecl.index][ver];
  const rawCodewords = Math.floor(getNumRawDataModules(ver) / 8);
  const numShortBlocks = numBlocks - (rawCodewords % numBlocks);
  const shortBlockLen = Math.floor(rawCodewords / numBlocks);

  const blocks = [];
  const divisor = rsComputeDivisor(blockEccLen);
  for (let i = 0, k = 0; i < numBlocks; i += 1) {
    const dat = data.slice(k, k + shortBlockLen - blockEccLen + (i < numShortBlocks ? 0 : 1));
    k += dat.length;
    const ecc = rsComputeRemainder(dat, divisor);
    if (i < numShortBlocks) dat.push(0);
    blocks.push(dat.concat(ecc));
  }

  const result = [];
  for (let i = 0; i < blocks[0].length; i += 1) {
    blocks.forEach((block, j) => {
      if (i !== shortBlockLen - blockEccLen || j >= numShortBlocks) result.push(block[i]);
    });
  }
  return result;
};

/* ---------- matrix ---------- */
const getAlignmentPositions = (ver, size) => {
  if (ver === 1) return [];
  const numAlign = Math.floor(ver / 7) + 2;
  const step = Math.ceil((ver * 4 + 4) / (numAlign * 2 - 2)) * 2;
  const result = [6];
  for (let pos = size - 7; result.length < numAlign; pos -= step) result.splice(1, 0, pos);
  return result;
};

const createMatrices = (ver) => {
  const size = ver * 4 + 17;
  const modules = [];
  const isFunction = [];
  for (let y = 0; y < size; y += 1) {
    modules.push(new Array(size).fill(false));
    isFunction.push(new Array(size).fill(false));
  }

  const setFunction = (x, y, dark) => {
    modules[y][x] = dark;
    isFunction[y][x] = true;
  };

  const drawFinder = (cx, cy) => {
    for (let dy = -4; dy <= 4; dy += 1) {
      for (let dx = -4; dx <= 4; dx += 1) {
        const dist = Math.max(Math.abs(dx), Math.abs(dy));
        const x = cx + dx;
        const y = cy + dy;
        if (x >= 0 && x < size && y >= 0 && y < size) setFunction(x, y, dist !== 2 && dist !== 4);
      }
    }
  };

  const drawAlignment = (cx, cy) => {
    for (let dy = -2; dy <= 2; dy += 1) {
      for (let dx = -2; dx <= 2; dx += 1) {
        setFunction(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
      }
    }
  };

  // timing patterns
  for (let i = 0; i < size; i += 1) {
    setFunction(6, i, i % 2 === 0);
    setFunction(i, 6, i % 2 === 0);
  }

  drawFinder(3, 3);
  drawFinder(size - 4, 3);
  drawFinder(3, size - 4);

  const align = getAlignmentPositions(ver, size);
  const n = align.length;
  for (let i = 0; i < n; i += 1) {
    for (let j = 0; j < n; j += 1) {
      if (!((i === 0 && j === 0) || (i === 0 && j === n - 1) || (i === n - 1 && j === 0))) {
        drawAlignment(align[i], align[j]);
      }
    }
  }

  return { size, modules, isFunction, setFunction };
};

const drawFormatBits = (matrices, ecl, mask) => {
  const { size, setFunction } = matrices;
  const data = (ecl.formatBits << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i += 1) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  const bits = ((data << 10) | rem) ^ 0x5412;

  for (let i = 0; i <= 5; i += 1) setFunction(8, i, getBit(bits, i));
  setFunction(8, 7, getBit(bits, 6));
  setFunction(8, 8, getBit(bits, 7));
  setFunction(7, 8, getBit(bits, 8));
  for (let i = 9; i < 15; i += 1) setFunction(14 - i, 8, getBit(bits, i));

  for (let i = 0; i < 8; i += 1) setFunction(size - 1 - i, 8, getBit(bits, i));
  for (let i = 8; i < 15; i += 1) setFunction(8, size - 15 + i, getBit(bits, i));
  setFunction(8, size - 8, true);
};

const drawVersion = (matrices, ver) => {
  if (ver < 7) return;
  const { size, setFunction } = matrices;
  let rem = ver;
  for (let i = 0; i < 12; i += 1) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  const bits = (ver << 12) | rem;
  for (let i = 0; i < 18; i += 1) {
    const dark = getBit(bits, i);
    const a = size - 11 + (i % 3);
    const b = Math.floor(i / 3);
    setFunction(a, b, dark);
    setFunction(b, a, dark);
  }
};

const drawCodewords = (matrices, data) => {
  const { size, modules, isFunction } = matrices;
  let i = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert += 1) {
      for (let j = 0; j < 2; j += 1) {
        const x = right - j;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? size - 1 - vert : vert;
        if (!isFunction[y][x] && i < data.length * 8) {
          modules[y][x] = getBit(data[i >>> 3], 7 - (i & 7));
          i += 1;
        }
      }
    }
  }
};

const applyMask = (matrices, mask) => {
  const { size, modules, isFunction } = matrices;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      let invert;
      switch (mask) {
        case 0: invert = (x + y) % 2 === 0; break;
        case 1: invert = y % 2 === 0; break;
        case 2: invert = x % 3 === 0; break;
        case 3: invert = (x + y) % 3 === 0; break;
        case 4: invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break;
        case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break;
        case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break;
        default: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break;
      }
      if (!isFunction[y][x] && invert) modules[y][x] = !modules[y][x];
    }
  }
};

const FINDER_A = [true, false, true, true, true, false, true, false, false, false, false];
const FINDER_B = [false, false, false, false, true, false, true, true, true, false, true];

const matchesAt = (line, start, pattern) => {
  for (let i = 0; i < pattern.length; i += 1) {
    if (line[start + i] !== pattern[i]) return false;
  }
  return true;
};

const getPenalty = (modules, size) => {
  let penalty = 0;

  const scanLine = (line) => {
    // rule 1: runs of 5 or more
    let runLength = 1;
    for (let i = 1; i < line.length; i += 1) {
      if (line[i] === line[i - 1]) {
        runLength += 1;
        if (runLength === 5) penalty += 3;
        else if (runLength > 5) penalty += 1;
      } else {
        runLength = 1;
      }
    }
    // rule 3: finder-like patterns
    for (let i = 0; i + 11 <= line.length; i += 1) {
      if (matchesAt(line, i, FINDER_A) || matchesAt(line, i, FINDER_B)) penalty += 40;
    }
  };

  for (let y = 0; y < size; y += 1) scanLine(modules[y]);
  for (let x = 0; x < size; x += 1) scanLine(modules.map((row) => row[x]));

  // rule 2: 2x2 blocks
  for (let y = 0; y < size - 1; y += 1) {
    for (let x = 0; x < size - 1; x += 1) {
      const color = modules[y][x];
      if (color === modules[y][x + 1] && color === modules[y + 1][x] && color === modules[y + 1][x + 1]) {
        penalty += 3;
      }
    }
  }

  // rule 4: dark / light balance
  let dark = 0;
  modules.forEach((row) => row.forEach((value) => { if (value) dark += 1; }));
  const total = size * size;
  const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
  penalty += Math.max(0, k) * 10;

  return penalty;
};

/* ---------- public ---------- */
export const makeQr = (text, level = "M") => {
  const ecl = ECC[level] || ECC.M;
  const bytes = toUtf8Bytes(text);

  let ver = 1;
  for (; ver <= MAX_VERSION; ver += 1) {
    const needBits = 4 + (ver <= 9 ? 8 : 16) + bytes.length * 8;
    if (needBits <= getNumDataCodewords(ver, ecl) * 8) break;
  }
  if (ver > MAX_VERSION) {
    throw new Error("QR text is too long");
  }

  const data = buildDataCodewords(bytes, ver, ecl);
  const allCodewords = addEccAndInterleave(data, ver, ecl);

  const base = createMatrices(ver);
  drawFormatBits(base, ecl, 0);
  drawVersion(base, ver);
  drawCodewords(base, allCodewords);

  let bestMask = 0;
  let bestPenalty = Infinity;
  let best = null;

  for (let mask = 0; mask < 8; mask += 1) {
    const trial = {
      ...base,
      modules: base.modules.map((row) => row.slice()),
      isFunction: base.isFunction,
    };
    trial.setFunction = (x, y, dark) => {
      trial.modules[y][x] = dark;
    };
    applyMask(trial, mask);
    drawFormatBits(trial, ecl, mask);
    const penalty = getPenalty(trial.modules, base.size);
    if (penalty < bestPenalty) {
      bestPenalty = penalty;
      bestMask = mask;
      best = trial.modules;
    }
  }

  return { size: base.size, modules: best, version: ver, mask: bestMask };
};

// SVG path for all dark squares (1 unit = 1 module). Horizontal runs are merged.
export const qrToPath = (modules) => {
  let d = "";
  modules.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      if (row[x]) {
        let end = x;
        while (end < row.length && row[end]) end += 1;
        d += `M${x} ${y}h${end - x}v1h-${end - x}z`;
        x = end;
      } else {
        x += 1;
      }
    }
  });
  return d;
};